import React from 'react';
import { getJSClient } from '../js_client';
import logger from '../../utils/logging';
import {OPERATION_STATUS} from './common/constants';
import {downloadFile} from '../../utilities';
import { saveReportScreen } from './utils/save_report';
import { getColumnFormats } from './utils/formatting';
import {
  reportPreferences,
  getColumnSets,
  deriveColumnSets,
  getVisibleColumns,
  findColumnSetItems,
} from './utils/report_preferences';
import { extractColumnKey } from './utils/composite_keys';
import { pullDataWithRetryAndTimeout } from './utils/load_data';

/**
 * Holds the common functionalities that are required
 * to generate/download/save an accuracy report. Accepts
 * a view component as `props.viewComponent` which is responsible
 * for generating and rendering the actual report
 * (i.e. Component Report, PL Report, etc.).
 *
 * Listed below is how this class works in general -
 *
 *  - It takes  in `props.requestPayloads`, makes API calls
 *    for each payload and saves the responses in `this.responsePayloads`
 *    Failures are retried up till a specified max no of times.
 *
 * -  The parameters `props.requestPayloads` and `this.responsePayloads`
 *    are passed into the `props.viewComponent`. This `viewComponent` is
 *    responsible for rendering the report based on the specified parameters.
 *    The `viewComponent` can transform the data as per the requirements of
 *    the specified view (i.e. Component or PL report)
 *
 * -  Certain common methods - such as saving the report, downloading
 *    the report, saving column preferences, etc. are also handled by
 *    this class. The `viewComponent` can perform the report specific
 *    actions (i.e. transforming the data) before calling methods in
 *    this class to initiate actions such as save, download, etc.
 */
export class AccuracyReport extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      allPayloadsLoaded: false,
      loadError: false,
      displayInMillions: true,
      downloadStatus: OPERATION_STATUS.NONE,
      saveReportStatus: OPERATION_STATUS.NONE,
    };
    this.responsePayloads = [];
    this.incompleteResponsePayloads = [];

    this.columnFormats = getColumnFormats(this.props.columnsJson);
    this.reportPreferences = reportPreferences(this.props.savedPreferences, this.props.columnsJson);
    this.columnSets = getColumnSets(
      this.props.columnsJson,
      this.props.columnSetsToLoad,
      this.props.columnSetOverridesToLoad,
      this.props.payload.report.toUpperCase()
    );

    this.visibleColumns = getVisibleColumns(this.reportPreferences, this.props.requestPayloads, this.props.liveSnapshot);
  }

  componentDidMount = () => {
    this.client = getJSClient();
    this.initialClientTasks();
  };

  componentDidUpdate = (prevProps) => {
    if (prevProps.downloadData !== this.props.downloadData && this.props.downloadData) {
      logger.info('Downloading report.');
      this.setState({ downloadStatus: OPERATION_STATUS.IN_PROGRESS });
    }

    if (prevProps.saveReport !== this.props.saveReport && this.props.saveReport) {
      logger.info('Saving report.');
      this.setState({ saveReportStatus: OPERATION_STATUS.IN_PROGRESS });
    }
  };

  initialClientTasks = () => {
    const { requestPayloads } = this.props;

    for (let i = 0; i < requestPayloads.length; i++) {
      pullDataWithRetryAndTimeout(
        i,
        0,
        this.client,
        requestPayloads,
        this.responsePayloads,
        this.incompleteResponsePayloads,
        this.onAllPayloadsLoaded,
        this.onLoadDone,
        this.onLoadError
      );
    }
  };

  onAllPayloadsLoaded = () => {
    this.setState({ allPayloadsLoaded: true });
  };

  onLoadDone = () => {
    const reportType = this.props.payload.report.toUpperCase();
    this.props.onLoadDone({ reportType });
  };

  onLoadError = () => {
    this.setState({ loadError: true });
  }

  onDownloadDataSuccess = () => {
    this.setState({ downloadStatus: OPERATION_STATUS.SUCESS });
    this.props.resetButtons();
  };

  onDownloadDataFailure = () => {
    this.setState({ downloadStatus: OPERATION_STATUS.FAIL });
    this.props.resetButtons();
  };

  onDownloadDataCancel = () => {
    this.setState({ downloadStatus: OPERATION_STATUS.NONE });
    this.props.resetButtons();
  };

  onSubmitSave = (report) => {
    this.client.saveReport(this.onSaveReportSuccess, this.onSaveReportFail, { reportToSave: report });
  };

  onCancelSave = () => {
    this.setState({ saveReportStatus: OPERATION_STATUS.NONE });
    this.props.resetButtons();
  };

  onSaveReportSuccess = (report) => {
    this.setState({ saveReportStatus: OPERATION_STATUS.SUCESS });
    this.props.onSaveReport(report);
    this.props.resetButtons();
  };

  onSaveReportFail = () => {
    this.setState({ saveReportStatus: OPERATION_STATUS.FAIL });
    this.props.resetButtons();
  };

  onContentSelectionChange = (detail, selectedColumnSets) => {
    this.visibleColumns = detail.visibleContent;
    this.savePreferences(
      this.visibleColumns.map((c) => extractColumnKey(c)),
      selectedColumnSets
    );
  };

  onContentSelectorOptionsRegenerate = (contentSelectorOptions) => {
    if (contentSelectorOptions && contentSelectorOptions.length) {
      this.visibleColumns = contentSelectorOptions[0].options
        .filter((option) => option.visible)
        .map((option) => option.id);
    }
  };

  savePreferences = (columns, columnSets) => {
    const reportType = this.props.payload.report.toUpperCase();
    const columnSetIds = (columnSets || []).map((c) => (c.id ? c.id : c));
    const savedReportPreferences = this.props.savedPreferences && this.props.savedPreferences.reportPreferences;

    const hasSavedPreferencesForReportType =
      savedReportPreferences && savedReportPreferences.find((p) => p.reportType.toUpperCase() === reportType);

    const preferences = [
      ...(savedReportPreferences || []),
      ...(!hasSavedPreferencesForReportType ? [{ reportType }] : []),
    ];

    const columnsToSave = [...new Set(columns || [])];
    const reportPreferences = preferences.map((p) => {
      return p.reportType.toUpperCase() === reportType
        ? { columnSets: columnSetIds, columns: columnsToSave, reportType: reportType.toLowerCase() }
        : p;
    });

    const savePreferencesPayload = {
      preferencesToSave: {
        reportPreferences,
        user: this.props.user,
      },
    };

    this.client.saveUserPreferences(
      () => this.onSavePreferencesSuccess(savePreferencesPayload),
      this.onSavePreferencesFailure,
      savePreferencesPayload,
      this.props.user
    );
  };

  onSavePreferencesSuccess = (savePreferencesPayload) => {
    this.props.onSavePreferences(savePreferencesPayload.preferencesToSave);
  };

  onSavePreferencesFailure = () => {
    this.props.resetButtons();
  };

  onToggleMillions = () => {
    this.setState({ displayInMillions: !this.state.displayInMillions });
  };

  onSelectedColumnSetsChanged = (selectedColumnSetsByUser) => {
    this.columnSets = { ...this.columnSets, selectedColumnSetsByUser };

    const reportType = this.props.payload.report.toUpperCase();
    const columnSetItems = findColumnSetItems(this.props.columnsJson, selectedColumnSetsByUser, reportType);
    const columns = [...new Set((columnSetItems || []).map((s) => s.columns).flat())];
    const columnsToSave = columns && columns.length ? columns :
        getVisibleColumns(this.reportPreferences, this.props.requestPayloads, this.props.liveSnapshot);

    this.savePreferences(columnsToSave, columnSetItems);
  };

  render() {
    const { allPayloadsLoaded, loadError } = this.state;
    const { payload, snapshots, user, reportsList, columnsJson } = this.props;
    const ViewComponent = this.props.viewComponent;
    const reportType = payload.report.toUpperCase();

    return (
      <div>
        {this.state.saveReportStatus === OPERATION_STATUS.IN_PROGRESS &&
          saveReportScreen({
            payload,
            snapshots,
            user,
            reportsList,
            aggregatedItems: {},
            columnSets: this.columnSets,
            reportPreferences: this.reportPreferences,
            visibleColumns: this.visibleColumns,
            reportType,
            columnsJson,
            onSubmitSave: this.onSubmitSave,
            onCancelSave: this.onCancelSave,
          })}
        <ViewComponent
          responsePayloads={this.responsePayloads}
          loading={!allPayloadsLoaded}
          loadError={loadError}
          downloadStatus={this.state.downloadStatus}
          onDownloadDataSuccess={this.onDownloadDataSuccess}
          onDownloadDataFailure={this.onDownloadDataFailure}
          onDownloadDataCancel={this.onDownloadDataCancel}
          downloadFile={downloadFile}
          columnFormats={this.columnFormats}
          reportPreferences={this.reportPreferences}
          columnSets={this.columnSets}
          onContentSelectionChange={this.onContentSelectionChange}
          onContentSelectorOptionsRegenerate={this.onContentSelectorOptionsRegenerate}
          onToggleMillions={this.onToggleMillions}
          displayInMillions={this.state.displayInMillions}
          client={this.client}
          selectedColumnSets={deriveColumnSets(this.columnSets, this.reportPreferences, reportType)}
          onSelectedColumnSetsChanged={this.onSelectedColumnSetsChanged}
          visibleColumns={this.visibleColumns}
          savePreferences={this.savePreferences}
          {...this.props}
        />
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/reports/accuracy_report.js